export const LOW_POWER_PRESENTATION_FPS = 30;

export interface ForegroundFrameDriver {
  runFrame(frameStartedAtMs: number): void;
  setLowPowerMode(enabled: boolean): void;
  setStandbyMode(enabled: boolean): void;
}

interface CreateForegroundFrameDriverOptions<TFrame> {
  readonly lowPowerMode: boolean;
  readonly advanceFrame: () => TFrame;
  readonly presentFrame: (frame: TFrame) => void;
}

export function createForegroundFrameDriver<TFrame>(
  options: CreateForegroundFrameDriverOptions<TFrame>,
): ForegroundFrameDriver {
  const lowPowerPresentationIntervalMs = 1_000 / LOW_POWER_PRESENTATION_FPS;
  let lowPowerMode = options.lowPowerMode;
  let standbyMode = false;
  let nextLowPowerPresentationAtMs: number | null = null;

  const isPresentationDue = (frameStartedAtMs: number): boolean => {
    if (!lowPowerMode) {
      return true;
    }
    if (nextLowPowerPresentationAtMs === null) {
      nextLowPowerPresentationAtMs =
        frameStartedAtMs + lowPowerPresentationIntervalMs;
      return true;
    }
    if (frameStartedAtMs < nextLowPowerPresentationAtMs) {
      return false;
    }

    const elapsedIntervals =
      Math.floor(
        (frameStartedAtMs - nextLowPowerPresentationAtMs) /
          lowPowerPresentationIntervalMs,
      ) + 1;
    nextLowPowerPresentationAtMs +=
      elapsedIntervals * lowPowerPresentationIntervalMs;
    return true;
  };

  return {
    runFrame(frameStartedAtMs): void {
      const frame = options.advanceFrame();
      if (!Number.isFinite(frameStartedAtMs)) {
        throw new Error("frameStartedAtMs must be finite");
      }
      if (standbyMode) {
        return;
      }
      if (isPresentationDue(frameStartedAtMs)) {
        options.presentFrame(frame);
      }
    },

    setLowPowerMode(enabled): void {
      if (lowPowerMode === enabled) {
        return;
      }
      lowPowerMode = enabled;
      nextLowPowerPresentationAtMs = null;
    },

    setStandbyMode(enabled): void {
      if (standbyMode === enabled) {
        return;
      }
      standbyMode = enabled;
      nextLowPowerPresentationAtMs = null;
    },
  };
}
